import { Flex, Heading, Button, Checkbox } from "@chakra-ui/react";
import { DayOfWeek, LocalTime } from "@js-joda/core";
import React from "react";
import { AddButton } from "./AddButton";
import { TimeTangePicker } from "./TimeSlotTimeRagePicker";
import { patientTaskCustomizeDayTimeParamsUtils } from "./PatientTaskCustomizeDayTimeParams.utils";
import { PatientTaskCustomizeDayTimeParamsTypes } from "./PatientTaskCustomizeDayTimeParams.types";
import immer from "immer";
import { toHumanCase } from "../../shared/utils";

export function PatientTaskCustomizeDayTimeParamsPattern(props: {
  dateRange: PatientTaskCustomizeDayTimeParamsTypes.DateRange;
  dayTimeScheduleOverrideParams: PatientTaskCustomizeDayTimeParamsTypes.DayTimeParamsType.Pattern;
  onChange: (params: PatientTaskCustomizeDayTimeParamsTypes.DayTimeParamsType.Pattern) => void;
}) {
  const [differentTimePerDay, setDifferentTimePerDay] = React.useState(
    isCurrentlyDifferentTimePerDay(props.dayTimeScheduleOverrideParams.params)
  );

  const activeDaysOfWeek = new Set(
    props.dayTimeScheduleOverrideParams.params.map((x) => x.dayOfWeek)
  );
  const daysOfWeekInRange = patientTaskCustomizeDayTimeParamsUtils.getDaysOfWeekInRange(
    props.dateRange
  );

  const handleToggleDay = (dayOfWeek: DayOfWeek) => {
    if (!daysOfWeekInRange.has(dayOfWeek)) {
      return;
    }

    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      const foundDay = draft.params.findIndex((param) => param.dayOfWeek === dayOfWeek);
      if (foundDay > -1) {
        draft.params.splice(foundDay, 1);
      } else {
        if (differentTimePerDay) {
          draft.params.push({
            dayOfWeek,
            timeSlots: [{ startTime: LocalTime.of(9), endTime: LocalTime.of(13) }],
          });
        } else {
          draft.params.push({
            dayOfWeek,
            timeSlots: newDayTimeParams.params.at(0)?.timeSlots ?? [],
          });
        }
      }
    });
    props.onChange(newDayTimeParams);
  };

  const handleRemoveTimeSlot = (dayOfWeek: DayOfWeek, timeSlotIndex: number) => {
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      const foundDay = draft.params.findIndex((param) => param.dayOfWeek === dayOfWeek);
      if (foundDay > -1) {
        draft.params[foundDay].timeSlots.splice(timeSlotIndex, 1);
      }
    });
    props.onChange(newDayTimeParams);
  };

  const handleUpdateTimeSlot = (
    dayOfWeek: DayOfWeek,
    timeSlotIndex: number,
    startTime: LocalTime,
    endTime: LocalTime
  ) => {
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      const foundDay = draft.params.findIndex((param) => param.dayOfWeek === dayOfWeek);
      if (foundDay > -1) {
        draft.params[foundDay].timeSlots[timeSlotIndex] = { startTime, endTime };
      }
    });
    props.onChange(newDayTimeParams);
  };

  const handleAddTimeSlot = (dayOfWeek: DayOfWeek) => {
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      const foundDay = draft.params.findIndex((param) => param.dayOfWeek === dayOfWeek);
      if (foundDay > -1) {
        draft.params[foundDay].timeSlots.push({
          startTime: LocalTime.of(9),
          endTime: LocalTime.of(13),
        });
      }
    });
    props.onChange(newDayTimeParams);
  };

  const handleToggleDifferentTimePerDay = () => {
    const newVal = !differentTimePerDay;
    setDifferentTimePerDay(newVal);
    if (!newVal) {
      props.onChange({
        type: "Pattern",
        params: Array.from(activeDaysOfWeek).map((dayOfWeek) => ({
          dayOfWeek,
          timeSlots: [
            props.dayTimeScheduleOverrideParams.params.at(0)?.timeSlots.at(0) ?? {
              startTime: LocalTime.of(9),
              endTime: LocalTime.of(13),
            },
          ],
        })),
      });
    }
  };

  const handleRemoveEverydayTimeSlot = (timeSlotIndex: number) => {
    // go over all the timeSlots and remove the timeSlots at the index
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      draft.params.forEach((param) => {
        param.timeSlots.splice(timeSlotIndex, 1);
      });
    });
    props.onChange(newDayTimeParams);
  };

  const handleUpdateEverydayTimeSlotTime = (
    timeSlotIndex: number,
    startTime: LocalTime,
    endTime: LocalTime
  ) => {
    // go over all the timeSlots and update the timeSlot at the index
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      draft.params.forEach((param) => {
        param.timeSlots[timeSlotIndex] = { startTime, endTime };
      });
    });
    props.onChange(newDayTimeParams);
  };

  const handleAddEverydayTimeSlot = () => {
    // go over all the timeSlots and add a new timeSlot
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      draft.params.forEach((param) => {
        param.timeSlots.push({
          startTime: LocalTime.of(9),
          endTime: LocalTime.of(13),
        });
      });
    });
    props.onChange(newDayTimeParams);
  };

  return (
    <Flex flexDirection="column" gap={4}>
      <Flex gap={2}>
        {patientTaskCustomizeDayTimeParamsUtils.allDaysOfWeek.map((dow) => (
          <Button
            key={dow.toString()}
            borderRadius="3xl"
            colorScheme={activeDaysOfWeek.has(dow) ? "blue" : "gray"}
            flex={1}
            fontSize="sm"
            isDisabled={!daysOfWeekInRange.has(dow)}
            variant="outline"
            onClick={() => handleToggleDay(dow)}
          >
            {toHumanCase(dow.toString().slice(0, 2))}
          </Button>
        ))}
      </Flex>
      <Flex direction="column" gap={4}>
        <Flex>
          <Checkbox isChecked={differentTimePerDay} onChange={handleToggleDifferentTimePerDay}>
            Different time per day
          </Checkbox>
        </Flex>
        {differentTimePerDay ? (
          props.dayTimeScheduleOverrideParams.params.map(({ dayOfWeek, timeSlots }) => (
            <Flex key={dayOfWeek.toString()} direction="column">
              <Heading size="xs">{dayOfWeek.toString()}</Heading>
              {timeSlots.map((timeSlot, index) => (
                <TimeTangePicker
                  key={`${dayOfWeek.toString()}${timeSlot.startTime.toString()}${timeSlot.endTime.toString()}${index}`}
                  timeSlot={timeSlot}
                  onRemoveTimeSlot={() => handleRemoveTimeSlot(dayOfWeek, index)}
                  onUpdateTimeSlot={(startTime, endTime) =>
                    handleUpdateTimeSlot(dayOfWeek, index, startTime, endTime)
                  }
                />
              ))}
              <AddButton onClick={() => handleAddTimeSlot(dayOfWeek)}>Add Time Slot</AddButton>
            </Flex>
          ))
        ) : (
          <Flex direction="column">
            <Heading size="xs">Everyday</Heading>
            {props.dayTimeScheduleOverrideParams.params.at(0)?.timeSlots.map((timeSlot, index) => (
              <TimeTangePicker
                key={`everyday${timeSlot.startTime}${timeSlot.endTime}${index}`}
                timeSlot={timeSlot}
                onRemoveTimeSlot={() => handleRemoveEverydayTimeSlot(index)}
                onUpdateTimeSlot={(startTime, endTime) =>
                  handleUpdateEverydayTimeSlotTime(index, startTime, endTime)
                }
              />
            ))}
            <AddButton onClick={handleAddEverydayTimeSlot}>Add Time Slot</AddButton>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

function isCurrentlyDifferentTimePerDay(
  params: PatientTaskCustomizeDayTimeParamsTypes.DayTimeParamsType.Pattern["params"]
): boolean {
  if (params.length === 0) return false;

  const referenceTimeSlots = params[0].timeSlots;

  for (let i = 1; i < params.length; i++) {
    const currentTimeSlots = params[i].timeSlots;

    if (currentTimeSlots.length !== referenceTimeSlots.length) return true;

    for (let j = 0; j < referenceTimeSlots.length; j++) {
      if (
        currentTimeSlots[j].startTime !== referenceTimeSlots[j].startTime ||
        currentTimeSlots[j].endTime !== referenceTimeSlots[j].endTime
      ) {
        return true;
      }
    }
  }

  return false;
}
